import { lazy, type ComponentType, type LazyExoticComponent } from 'react'

import { promptReload } from '@/lib/promptReload'

// The shapes browsers emit when a module script cannot be fetched or parsed.
// Chrome/Safari say "Failed to fetch dynamically imported module", Firefox says
// "error loading dynamically imported module", Safari also says "Importing a
// module script failed", and bundler runtimes say "Loading chunk N failed".
// Anything outside this set is a real fault inside the module — the per-tab
// error boundary owns it, and a reload prompt would only hide it.
const CHUNK_LOAD_FAILURE =
  /dynamically imported module|Importing a module script failed|Loading chunk/i

/**
 * React.lazy that survives a deploy. Code splitting names chunks by content
 * hash, so after a release the chunk a long-open tab wants is gone and its
 * dynamic import rejects.
 *
 * React CACHES that rejection: re-rendering the boundary replays the same
 * failure, so there is no in-place retry to wait for. The recovery path is the
 * prompt — reload onto the new build. The tab itself still lands in
 * TabPanel's error boundary, which is what keeps the failure to one tab.
 *
 * `name` becomes the component's displayName so the route guard chains stay
 * readable (React.lazy types are otherwise anonymous).
 */
// The constraint is React.lazy's own. Narrowing it to `unknown`/`object` makes
// TS infer the props as `never` at every call site. T is still inferred
// exactly per call, so no `any` reaches a caller.
// oxlint-disable-next-line typescript-eslint/no-explicit-any
export function lazyWithReload<T extends ComponentType<any>>(
  name: string,
  load: () => Promise<{ default: T }>
): LazyExoticComponent<T> & { displayName: string } {
  const Lazy = lazy(() =>
    load().catch((error: unknown) => {
      if (error instanceof Error && CHUNK_LOAD_FAILURE.test(error.message)) {
        promptReload(
          'This tab could not load part of the app because a new version was deployed. Reload to continue.'
        )
      }
      throw error
    })
  )
  return Object.assign(Lazy, { displayName: name })
}
